// Admin UI for adding an existing user to a tenant with a role — the
// tenant_users junction table previously had no write path outside migration
// seeders (see docs/implementation-plans for this feature). Reuses
// TenantManagement.js's SectionCard embed pattern like PropertyVisibilityManager
// and react-select for the user/role pickers per the frontend's established
// dropdown convention.
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import { MdDeleteOutline } from "react-icons/md";
import client from "../api/client";
import ConfirmDialog from "./ConfirmDialog";
import TableSkeleton from "./TableSkeleton";
import ButtonLoader from "./ButtonLoader";
import { withMinDuration } from "../utils";
import { ROLES, getStoredIsSuperAdmin } from "../constants/roles";

// Only roles updateRole can actually push to HubSpot - super_admin/moderator
// have no HubSpot equivalent, so they're not offered as a manual pick here.
const HUBSPOT_ROLE_STRING_BY_NAME = {
  canvasser: ROLES.CANVASSER,
  account_manager: ROLES.ACCOUNT_MANAGER,
  regional_manager: ROLES.REGIONAL_MANAGER,
};

const PAGE_SIZE = 10;

const ROLE_LABELS = {
  super_admin: "Super Admin",
  account_manager: "Account Manager",
  regional_manager: "Regional Manager",
  canvasser: "Canvasser",
  moderator: "Moderator",
};

// Keeps both pickers a fixed width/height instead of react-select's default
// content-based growth, which was stretching the row on long user/role labels.
// menuPortalTarget renders the open dropdown into document.body instead of
// inline, so it isn't clipped by the table's overflow-auto scroll container
// right below it.
// react-select doesn't accept Tailwind classes for its internal parts, so the
// selected/focused option colors are read from the same tenant-themeable
// --canvas-primary-rgb CSS variable useTenantTheme.js applies, instead of
// react-select's default blue.
const getPrimaryChannels = () => {
  const rgb = getComputedStyle(document.documentElement)
    .getPropertyValue("--canvas-primary-rgb")
    .trim();
  return rgb || "42 52 114";
};
const getPrimaryColor = (alpha = 1) => `rgba(${getPrimaryChannels().split(" ").join(", ")}, ${alpha})`;

const SELECT_STYLES = {
  container: (base) => ({ ...base, width: "100%" }),
  control: (base, state) => ({
    ...base,
    minHeight: "42px",
    borderColor: state.isFocused ? getPrimaryColor() : base.borderColor,
    boxShadow: state.isFocused ? `0 0 0 1px ${getPrimaryColor()}` : base.boxShadow,
    "&:hover": { borderColor: getPrimaryColor() },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? getPrimaryColor()
      : state.isFocused
      ? getPrimaryColor(0.1)
      : base.backgroundColor,
    color: state.isSelected ? "#fff" : base.color,
  }),
};

const TenantUsersManager = ({ tenantId, tenants = [], onTenantChange = () => {}, lockedTenant = null }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [removingId, setRemovingId] = useState(null);
  const [memberPendingRemove, setMemberPendingRemove] = useState(null);
  const [isSyncConfirmOpen, setIsSyncConfirmOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const isSuperAdmin = getStoredIsSuperAdmin();

  const loadMembers = async (isStale = () => false) => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await withMinDuration(client.get(`/tenants/${tenantId}/users`));
      if (isStale()) return;
      setMembers(res.data);
      setPage(1);
    } catch (error) {
      if (isStale()) return;
      toast.error(error.response?.data?.message || "Failed to load tenant members.");
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  // Guards against a slower response for a since-abandoned tenant selection
  // landing after a newer one and overwriting it with stale data.
  useEffect(() => {
    let cancelled = false;
    setSelectedUser(null);
    setSelectedRole(null);
    setMemberPendingRemove(null);
    loadMembers(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (lockedTenant) return;
    client
      .get("/tenants/roles")
      .then((res) => setRoles(res.data))
      .catch((error) => toast.error(error.response?.data?.message || "Failed to load roles."));
  }, [lockedTenant]);

  // Only offer roles updateRole can actually sync to HubSpot - a role picked
  // here always needs to land in HubSpot before the tenant-add call, so
  // super_admin/moderator (no HubSpot equivalent) aren't valid picks.
  const roleSelectOptions = roles
    .filter((r) => !!HUBSPOT_ROLE_STRING_BY_NAME[r.name])
    .map((r) => ({ value: r.id, label: ROLE_LABELS[r.name] || r.name, name: r.name }));

  // A regional manager only manages the users under them, never other regional managers.
  const visibleMembers = lockedTenant ? members.filter((m) => m.role?.name !== "regional_manager") : members;
  const pageCount = Math.max(1, Math.ceil(visibleMembers.length / PAGE_SIZE));
  const pagedMembers = visibleMembers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Small list: fetched once, filtered client-side by react-select.
  const loadUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const res = await client.get("/tenants/users/search");
      setAllUsers(res.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load users.");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (tenantId && !lockedTenant) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const userOptions = useMemo(() => {
    const activeMembers = members.filter((m) => m.status !== "disabled");
    const memberIds = new Set(activeMembers.map((m) => m.user_id));
    // Also by email: the users table can hold duplicate rows for the same person
    const memberEmails = new Set(activeMembers.map((m) => m.user?.email?.trim().toLowerCase()).filter(Boolean));
    return allUsers
      .filter((u) => !memberIds.has(u.id) && !memberEmails.has(u.email?.trim().toLowerCase()))
      .map((u) => {
        const baseLabel = `${u.firstname || ""} ${u.lastname || ""} (${u.email})`.trim();
        // Mirrors addUserToTenant: anyone but a super admin may hold only one tenant membership.
        const otherTenant = !u.is_super_admin
          ? (u.activeTenants || []).find((t) => Number(t.id) !== Number(tenantId))
          : null;
        return {
          value: u.id,
          label: otherTenant ? `${baseLabel} - Already in ${otherTenant.name}` : baseLabel,
          isDisabled: !!otherTenant,
          role: u.role,
          hubspot_user_id: u.hubspot_user_id,
          email: u.email,
        };
      });
  }, [allUsers, members, tenantId]);

  // The selected user's existing HubSpot-derived role (if any) is used as the
  // tenant role automatically — the manual role picker is only a fallback for
  // a user with no resolvable role yet. That role now has to be assigned via
  // updateRole (below) before the tenant-add call, rather than passed as a
  // role_id to addUserToTenant, which no longer accepts one. The user list only
  // contains users with a hubspot_user_id (see tenant.controller.js's
  // searchUsers), so every selectable user here can go through updateRole.
  const resolvedRole = selectedUser?.role || null;
  const needsManualRole = !!selectedUser && !resolvedRole;

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    if (needsManualRole && !selectedRole) return;
    setIsSaving(true);
    try {
      if (needsManualRole) {
        // Same call the Directory page's role change uses - sets user.role,
        // user_roles, and pushes the role to HubSpot. Must succeed before the
        // tenant add, since addUserToTenant now requires an already-resolvable
        // (i.e. already HubSpot-synced) role and no longer accepts a role_id.
        try {
          await client.put("/updateRole", {
            role: HUBSPOT_ROLE_STRING_BY_NAME[selectedRole.name],
            userId: selectedUser.hubspot_user_id,
            email: selectedUser.email,
          });
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to assign the selected role.");
          return;
        }
      }

      await client.post(`/tenants/${tenantId}/users`, { user_id: selectedUser.value });
      toast.success("User added to tenant.");
      setSelectedUser(null);
      setSelectedRole(null);
      await loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add user to tenant.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmRemoveUser = async () => {
    const member = memberPendingRemove;
    if (!member) return;
    setRemovingId(member.id);
    try {
      await client.patch(`/tenants/${tenantId}/users/${member.user_id}`);
      toast.success("User removed from tenant.");
      await loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to remove user from tenant.");
    } finally {
      setRemovingId(null);
      setMemberPendingRemove(null);
    }
  };

  const handleSyncHubspotUsers = async () => {
    setIsSyncing(true);
    try {
      const res = await client.post("/admin/sync-hubspot-users");
      const { totalUpdated = 0, totalDeactivated = 0, rolesUpdated = 0 } = res.data || {};
      toast.success(`HubSpot sync done: ${totalUpdated} active, ${totalDeactivated} marked unavailable, ${rolesUpdated} roles synced.`);
      await loadMembers();
      if (!lockedTenant) await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to sync users with HubSpot.");
    } finally {
      setIsSyncing(false);
      setIsSyncConfirmOpen(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <div className="flex items-center gap-3">
        <span className="w-1 h-6 rounded-full bg-canvas-primary shrink-0" />
        <h4 className="text-canvas-primary">Tenant Users</h4>
        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => setIsSyncConfirmOpen(true)}
            disabled={isSyncing}
            className="ml-auto flex items-center justify-center gap-2 border border-canvas-primary text-canvas-primary rounded px-4 py-2 uppercase font-outfit text-xs font-bold disabled:opacity-50"
          >
            {isSyncing && <ButtonLoader />}
            {isSyncing ? "Syncing..." : "Sync HubSpot Users"}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {!lockedTenant && (
          <form onSubmit={handleAddUser} className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-2 w-[220px] shrink-0">
              <h6 className="text-canvas-directoryText">Tenant</h6>
              <select
                className="border border-canvas-tersery rounded p-3 text-sm font-outfit h-[42px]"
                value={tenantId || ""}
                onChange={(e) => onTenantChange(e.target.value ? Number(e.target.value) : null)}
              >
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2 w-[420px] shrink-0">
              <h6 className="text-canvas-directoryText">User</h6>
              <Select
                options={userOptions}
                value={selectedUser}
                onChange={(user) => {
                  setSelectedUser(user);
                  setSelectedRole(null);
                }}
                isLoading={isLoadingUsers}
                placeholder="Search by name or email..."
                noOptionsMessage={() => "No matching users"}
                styles={SELECT_STYLES}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isDisabled={!tenantId}
              />
            </div>
            <div className="flex flex-col gap-2 w-[200px] shrink-0">
              <h6 className="text-canvas-directoryText flex items-center gap-1">
                Role
                {!needsManualRole && (
                  <span
                    className="w-3.5 h-3.5 rounded-full border border-canvas-secondery text-canvas-secondery text-[10px] leading-[13px] text-center cursor-help select-none"
                    title="Synced from HubSpot — not editable here."
                  >
                    i
                  </span>
                )}
              </h6>
              {needsManualRole ? (
                <Select
                  options={roleSelectOptions}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  placeholder="Select role..."
                  styles={SELECT_STYLES}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isDisabled={!tenantId}
                />
              ) : (
                <div
                  className="border border-canvas-tersery rounded p-3 text-sm font-outfit h-[42px] flex items-center text-canvas-directoryText bg-gray-50"
                  title="Synced from HubSpot — not editable here."
                >
                  {resolvedRole ? (ROLE_LABELS[resolvedRole] || resolvedRole) : "-"}
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={isSaving || !tenantId || !selectedUser || (needsManualRole && !selectedRole)}
              className="flex items-center justify-center gap-2 bg-canvas-primary text-canvas-white rounded px-6 py-3 uppercase font-outfit text-xs font-bold disabled:opacity-50 h-[42px]"
            >
              {isSaving && <ButtonLoader />}
              {isSaving ? "Adding..." : "+ Add User"}
            </button>
          </form>
        )}

        {!tenantId ? null : (
        <div className="border border-gray-200 rounded">
          <div className="overflow-auto max-h-[420px]">
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <colgroup>
                <col className="w-1/4" />
                <col className="w-1/3" />
                <col className="w-1/6" />
                {!lockedTenant && <col className="w-1/6" />}
              </colgroup>
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Name</th>
                  <th className="px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Email</th>
                  <th
                    className="px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase"
                    title="Reflects the user's current HubSpot role — managed in HubSpot, not editable here."
                  >
                    Role
                  </th>
                  {!lockedTenant && (
                    <th className="px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Action</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 transition-opacity duration-200 ease-in" key={loading ? "loading" : "loaded"}>
                {loading ? (
                  <TableSkeleton rows={4} columns={lockedTenant ? 3 : 4} />
                ) : visibleMembers.length === 0 ? (
                  <tr>
                    <td colSpan={lockedTenant ? 3 : 4} className="px-3 py-6 text-center font-outfit text-sm text-canvas-secondery">
                      No users have been added to this tenant yet.
                    </td>
                  </tr>
                ) : (
                  pagedMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-100">
                      <td className="px-3 py-2 truncate font-outfit text-sm">
                        {member.user ? `${member.user.firstname || ""} ${member.user.lastname || ""}`.trim() || "-" : "-"}
                      </td>
                      <td className="px-3 py-2 truncate font-outfit text-sm">{member.user?.email}</td>
                      <td className="px-3 py-2 truncate font-outfit text-sm">{ROLE_LABELS[member.role?.name] || member.role?.name}</td>
                      {!lockedTenant && (
                        <td className="px-3 py-2 truncate font-outfit text-sm">
                          <button
                            type="button"
                            onClick={() => setMemberPendingRemove(member)}
                            disabled={removingId === member.id}
                            aria-label={`Remove ${member.user?.email || "user"} from tenant`}
                            className="disabled:opacity-40"
                          >
                            <MdDeleteOutline className="w-6 h-6 text-canvas-danger" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && visibleMembers.length > 0 && (
            <div className="flex items-center justify-between px-3 py-3 border-t border-gray-200">
              <span className="text-xs text-canvas-secondery font-outfit">
                Page {page} of {pageCount} ({visibleMembers.length} users)
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs font-outfit font-bold uppercase border border-canvas-tersery rounded disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={page === pageCount}
                  className="px-3 py-1.5 text-xs font-outfit font-bold uppercase border border-canvas-tersery rounded disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(memberPendingRemove)}
        title="Remove user"
        message={`Remove ${
          memberPendingRemove?.user
            ? `${memberPendingRemove.user.firstname || ""} ${memberPendingRemove.user.lastname || ""}`.trim() || memberPendingRemove.user.email
            : "this user"
        } from this tenant? They will lose access to this tenant until re-added.`}
        confirmLabel="Remove"
        danger
        isConfirming={removingId !== null}
        onConfirm={handleConfirmRemoveUser}
        onCancel={() => setMemberPendingRemove(null)}
      />

      <ConfirmDialog
        open={isSyncConfirmOpen}
        title="Sync HubSpot users"
        message="Match every app user with HubSpot users. Users found in HubSpot are marked available; users no longer in HubSpot are marked unavailable and hidden from user lists."
        confirmLabel="Sync"
        isConfirming={isSyncing}
        onConfirm={handleSyncHubspotUsers}
        onCancel={() => setIsSyncConfirmOpen(false)}
      />
    </div>
  );
};

export default TenantUsersManager;
